import type { AdminPolicy, CreatePolicyRequest } from '@inflip/contracts/admin';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { Select } from '../components/select';
import { Button, EmptyState, ErrorBanner, PageHeader, inputCls } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import {
  POLICY_KIND_LABELS,
  POLICY_KIND_OPTIONS,
  policyKindFields,
  policyKindOf,
  type PolicyKind,
} from '../lib/policy-kind';
import { formatDate } from '../lib/time-zone';

/** 약관/방침 목록 — 문서 생성, 발행 상태 확인 */
export function PoliciesPage() {
  const { session } = useAdminAuth();
  const [rows, setRows] = useState<AdminPolicy[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreatePolicyRequest>({
    slug: '',
    title: '',
    isRequired: true,
    needsConsent: true,
    sortOrder: 0,
  });
  const [pending, setPending] = useState(false);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .listPolicies(session.accessToken)
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!session || pending) return;
    setPending(true);
    setError(null);
    try {
      await api.createPolicy(session.accessToken, form);
      setShowCreate(false);
      setForm({ slug: '', title: '', isRequired: true, needsConsent: true, sortOrder: 0 });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="약관 관리"
        description="약관·방침 문서를 만들고 발행 상태를 확인해요"
        actions={
          <Button
            variant={showCreate ? 'secondary' : 'primary'}
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? '닫기' : '+ 새 문서'}
          </Button>
        }
      />
      {error && <ErrorBanner message={error} />}

      {showCreate && (
        <form
          onSubmit={create}
          className="mb-6 flex flex-wrap items-end gap-3 rounded-lg bg-muted/60 p-5"
        >
          <div>
            <label className="text-xs font-semibold text-muted-foreground">slug (URL 식별자)</label>
            <input
              className={`mt-1 block w-44 ${inputCls}`}
              placeholder="service-terms"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              pattern="[a-z0-9-]+"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">문서명</label>
            <input
              className={`mt-1 block w-56 ${inputCls}`}
              placeholder="서비스 이용약관"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div>
            <span className="text-xs font-semibold text-muted-foreground">동의 방식</span>
            <Select
              label="동의 방식"
              className="mt-1 w-48"
              value={policyKindOf(form)}
              options={POLICY_KIND_OPTIONS}
              onValueChange={(v) => setForm({ ...form, ...policyKindFields(v as PolicyKind) })}
            />
          </div>
          <Button type="submit" disabled={pending}>
            생성 (v1 초안 자동 생성)
          </Button>
        </form>
      )}

      {rows.length === 0 && !showCreate && (
        <EmptyState message="문서가 없어요. 서비스 이용약관(service-terms)과 개인정보처리방침(privacy)부터 만들어주세요." />
      )}

      <div className="-mx-3 divide-y divide-border/60">
        {rows.map((p) => (
          <Link
            key={p.id}
            to="/policies/$policyId"
            params={{ policyId: p.id }}
            className="flex items-center gap-4 rounded-lg px-3 py-4 transition hover:bg-muted"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">
                {p.title}
                <span className="ml-1.5 font-mono text-xs font-medium text-muted-foreground">
                  /{p.slug}
                </span>
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {POLICY_KIND_LABELS[policyKindOf(p)]}
                {!p.isActive && ' · 비활성'}
              </p>
            </div>
            <span
              className={`shrink-0 text-xs font-bold ${
                p.publishedVersion ? 'text-emerald-600' : 'text-amber-600'
              }`}
            >
              {p.publishedVersion
                ? `v${p.publishedVersion} 발행 중 (${p.publishedAt ? formatDate(p.publishedAt) : ''})`
                : '발행본 없음 — 미노출'}
            </span>
          </Link>
        ))}
      </div>
    </main>
  );
}
